import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Table,
  Typography,
} from '@mui/joy';
import { api } from '../../lib/api';

interface RoleSummary {
  id: number;
  name: string;
  permissions: Record<string, string[]>;
  record_scopes: Record<string, string>;
  include_unassigned: boolean;
  is_system: boolean;
  user_count: number;
}

interface ResourceEntry { key: string; label: string; ownable?: boolean }
interface ResourceSection { section: string; resources: ResourceEntry[] }
interface ResourcesCatalog { actions: string[]; sections: ResourceSection[] }

const ACTION_LABELS: Record<string, string> = {
  view: 'View', create: 'Create', edit: 'Edit', delete: 'Delete',
};

export const RolesPermissionsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [roleName, setRoleName] = useState('');
  const [matrix, setMatrix] = useState<Record<string, string[]>>({});
  const [scopes, setScopes] = useState<Record<string, string>>({});
  const [includeUnassigned, setIncludeUnassigned] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  const { data: catalog, error: catalogError, refetch: retryCatalog } = useQuery<ResourcesCatalog>({
    queryKey: ['rbac-resources'],
    queryFn: async () => (await api.get<ResourcesCatalog>('/api/roles-resources/')).data,
    staleTime: Infinity,
  });

  const { data: roles, isLoading, error: rolesError, refetch: retryRoles } = useQuery<RoleSummary[]>({
    queryKey: ['rbac-roles'],
    queryFn: async () => {
      const response = await api.get<{ results?: RoleSummary[] } | RoleSummary[]>('/api/roles/');
      const data = response.data as { results?: RoleSummary[] } | RoleSummary[];
      return Array.isArray(data) ? data : (data.results || []);
    },
  });

  const selectedRole = useMemo(
    () => (roles || []).find((r) => r.id === selectedRoleId) || null,
    [roles, selectedRoleId],
  );

  // Select the first role once loaded
  useEffect(() => {
    if (!selectedRoleId && roles && roles.length > 0) {
      setSelectedRoleId(roles[0].id);
    }
  }, [roles, selectedRoleId]);

  // Hydrate the editor when the selection changes
  useEffect(() => {
    if (selectedRole) {
      setRoleName(selectedRole.name);
      setMatrix(selectedRole.permissions || {});
      setScopes(selectedRole.record_scopes || {});
      setIncludeUnassigned(!!selectedRole.include_unassigned);
      setIsDirty(false);
    }
  }, [selectedRole?.id, selectedRole?.name]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['rbac-roles'] });
  const apiError = (error: unknown, fallback: string) => {
    const data = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
    const first = data && (data.error || Object.values(data)[0]);
    toast.error(
      (Array.isArray(first) ? String(first[0]) : typeof first === 'string' ? first : '') || fallback,
    );
  };

  const createRole = useMutation({
    mutationFn: async (name: string) =>
      (await api.post<RoleSummary>('/api/roles/', { name, permissions: {} })).data,
    onSuccess: (created: RoleSummary) => {
      toast.success(`Role "${created.name}" created`);
      setNewRoleName('');
      invalidate();
      setSelectedRoleId(created.id);
    },
    onError: (error) => apiError(error, 'Could not create role'),
  });

  const saveRole = useMutation({
    mutationFn: async () => {
      // Scopes only make sense on resources with granted actions
      const prunedScopes = Object.fromEntries(
        Object.entries(scopes).filter(([resource]) => (matrix[resource] || []).length > 0),
      );
      return (await api.patch(`/api/roles/${selectedRoleId}/`, {
        name: roleName,
        permissions: matrix,
        record_scopes: prunedScopes,
        include_unassigned: includeUnassigned,
      })).data;
    },
    onSuccess: () => {
      toast.success('Role saved');
      setIsDirty(false);
      invalidate();
    },
    onError: (error) => apiError(error, 'Could not save role'),
  });

  const deleteRole = useMutation({
    mutationFn: async () => (await api.delete(`/api/roles/${selectedRoleId}/`)).data,
    onSuccess: () => {
      toast.success('Role deleted');
      setSelectedRoleId(null);
      invalidate();
    },
    onError: (error) => apiError(error, 'Could not delete role'),
  });

  const readOnly = !!selectedRole?.is_system;

  const grantedCount = useMemo(
    () => Object.values(matrix).reduce((sum, actions) => sum + actions.length, 0),
    [matrix],
  );

  const hasAction = (resource: string, action: string) =>
    (matrix[resource] || []).includes(action);

  const anyOwnScoped = useMemo(
    () => Object.entries(scopes).some(
      ([resource, scope]) => scope === 'own' && (matrix[resource] || []).length > 0,
    ),
    [scopes, matrix],
  );

  const setScope = (resource: string, scope: string | null) => {
    if (readOnly) return;
    setScopes((prev) => {
      const next = { ...prev };
      if (scope === 'own') next[resource] = 'own';
      else delete next[resource];
      return next;
    });
    setIsDirty(true);
  };

  const toggleAction = (resource: string, action: string) => {
    if (readOnly) return;
    setMatrix((prev) => {
      const current = new Set(prev[resource] || []);
      if (current.has(action)) current.delete(action);
      else current.add(action);
      const next = { ...prev };
      if (current.size) next[resource] = Array.from(current).sort();
      else delete next[resource];
      return next;
    });
    setIsDirty(true);
  };

  const toggleRow = (resource: string) => {
    if (readOnly || !catalog) return;
    setMatrix((prev) => {
      const full = (prev[resource] || []).length === catalog.actions.length;
      const next = { ...prev };
      if (full) delete next[resource];
      else next[resource] = [...catalog.actions];
      return next;
    });
    setIsDirty(true);
  };

  const toggleColumn = (action: string) => {
    if (readOnly || !catalog) return;
    const allResources = catalog.sections.flatMap((s) => s.resources.map((r) => r.key));
    const everyHasIt = allResources.every((r) => hasAction(r, action));
    setMatrix((prev) => {
      const next: Record<string, string[]> = { ...prev };
      for (const resource of allResources) {
        const current = new Set(next[resource] || []);
        if (everyHasIt) current.delete(action);
        else current.add(action);
        if (current.size) next[resource] = Array.from(current).sort();
        else delete next[resource];
      }
      return next;
    });
    setIsDirty(true);
  };

  if (catalogError || rolesError) return <Alert color="danger" endDecorator={<Button onClick={() => { void retryCatalog(); void retryRoles(); }}>Retry</Button>}>Could not load roles and permissions.</Alert>;

  if (isLoading || !catalog) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography level="h3" sx={{ mb: 0.5 }}>Roles &amp; Permissions</Typography>
      <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 3 }}>
        Define roles and grant each a set of permissions. Assign people under Users & permissions.
        Users without a role keep full member access; organization admins always have
        every permission. For Contacts, Sales Pipeline, and Jobs, the Records column can
        limit a role to only the records assigned to (or owned by) the user.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2.5, flexDirection: { xs: 'column', xl: 'row' } }}>
        {/* Roles list */}
        <Stack spacing={2} sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0 }}>
          <Sheet variant="outlined" sx={{ borderRadius: 'lg', overflow: 'hidden' }}>
            <Typography level="body-xs" sx={{ px: 2, pt: 1.5, pb: 0.5, textTransform: 'uppercase', fontWeight: 700, color: 'text.tertiary' }}>
              Roles
            </Typography>
            {(roles || []).map((role) => (
              <Box
                key={role.id}
                onClick={() => {
                  if (isDirty && !window.confirm('Discard unsaved changes to this role?')) return;
                  setSelectedRoleId(role.id);
                }}
                sx={{
                  px: 2, py: 1.25, cursor: 'pointer',
                  bgcolor: role.id === selectedRoleId ? 'primary.softBg' : 'transparent',
                  borderLeft: '3px solid',
                  borderColor: role.id === selectedRoleId ? 'primary.500' : 'transparent',
                  '&:hover': { bgcolor: role.id === selectedRoleId ? 'primary.softBg' : 'neutral.softBg' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography level="title-sm">{role.name}</Typography>
                  {role.is_system && (
                    <Chip size="sm" variant="soft" color="warning" sx={{ fontSize: '0.6rem' }}>SYSTEM</Chip>
                  )}
                </Box>
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  {Object.values(role.permissions || {}).reduce((n, a) => n + a.length, 0)} permissions
                  {' · '}{role.user_count} user{role.user_count === 1 ? '' : 's'}
                </Typography>
              </Box>
            ))}
          </Sheet>

          <Sheet variant="outlined" sx={{ borderRadius: 'lg', p: 2 }}>
            <Typography level="body-xs" sx={{ textTransform: 'uppercase', fontWeight: 700, color: 'text.tertiary', mb: 1 }}>
              New Role
            </Typography>
            <Input
              size="sm"
              placeholder="e.g. Sales Rep"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              sx={{ mb: 1 }}
            />
            <Button
              fullWidth
              size="sm"
              disabled={newRoleName.trim().length < 2}
              loading={createRole.isPending}
              onClick={() => createRole.mutate(newRoleName.trim())}
            >
              + Add Role
            </Button>
          </Sheet>
        </Stack>

        {/* Role editor */}
        <Sheet variant="outlined" sx={{ borderRadius: 'lg', p: { xs: 1.5, md: 2.5 }, flex: 1, minWidth: 0 }}>
          {!selectedRole ? (
            <Typography level="body-md" sx={{ color: 'text.tertiary', p: 3 }}>
              Select a role to edit its permissions, or create a new one.
            </Typography>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap', mb: 1.5 }}>
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <Typography level="body-xs" sx={{ textTransform: 'uppercase', fontWeight: 700, color: 'text.tertiary', mb: 0.5 }}>
                    Role Name
                  </Typography>
                  <Input
                    value={roleName}
                    disabled={readOnly}
                    onChange={(e) => { setRoleName(e.target.value); setIsDirty(true); }}
                  />
                </Box>
                {!readOnly && (
                  <>
                    <Button
                      loading={saveRole.isPending}
                      disabled={!isDirty || roleName.trim().length < 2}
                      onClick={() => saveRole.mutate()}
                    >
                      Save Role
                    </Button>
                    <Button
                      variant="outlined"
                      color="danger"
                      loading={deleteRole.isPending}
                      onClick={() => {
                        if (window.confirm(`Delete the "${selectedRole.name}" role? Users assigned to it must be reassigned first.`)) {
                          deleteRole.mutate();
                        }
                      }}
                    >
                      Delete Role
                    </Button>
                  </>
                )}
              </Box>

              {readOnly && (
                <Alert color="warning" variant="soft" sx={{ mb: 1.5 }}>
                  <strong>{selectedRole.name}</strong>&nbsp;is the system administrator role — it
                  always holds every permission and cannot be edited or deleted.
                </Alert>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                <Typography level="title-sm">
                  Permissions · {readOnly ? 'all' : grantedCount} granted
                </Typography>
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  Click a screen name or action header to toggle a whole row / column.
                </Typography>
              </Box>

              {anyOwnScoped && !readOnly && (
                <Alert variant="soft" color="primary" sx={{ mb: 1, display: 'block' }}>
                  <Checkbox
                    label="Can also see unassigned records (no owner or assignee)"
                    checked={includeUnassigned}
                    onChange={(e) => { setIncludeUnassigned(e.target.checked); setIsDirty(true); }}
                  />
                  <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.secondary' }}>
                    Applies to every screen set to &ldquo;Only theirs&rdquo; — viewing, editing,
                    and deleting are all limited to records assigned to the user.
                  </Typography>
                </Alert>
              )}

              {/* The matrix is a wide control grid — horizontal scroll on small screens */}
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="sm" sx={{ minWidth: 690, '--TableCell-paddingY': '6px' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '32%' }}>Screen</th>
                      {catalog.actions.map((action) => (
                        <th
                          key={action}
                          style={{ cursor: readOnly ? 'default' : 'pointer', textAlign: 'center' }}
                          onClick={() => toggleColumn(action)}
                        >
                          {ACTION_LABELS[action] || action}
                        </th>
                      ))}
                      <th style={{ width: 130, textAlign: 'center' }}>Records</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catalog.sections.map((section) => (
                      <React.Fragment key={section.section}>
                        <tr>
                          <td colSpan={2 + catalog.actions.length} style={{ background: 'var(--joy-palette-neutral-softBg)' }}>
                            <Typography level="body-xs" sx={{ textTransform: 'uppercase', fontWeight: 700, color: 'primary.600' }}>
                              {section.section}
                            </Typography>
                          </td>
                        </tr>
                        {section.resources.map((resource) => (
                          <tr key={resource.key}>
                            <td
                              style={{ cursor: readOnly ? 'default' : 'pointer', fontWeight: 600 }}
                              onClick={() => toggleRow(resource.key)}
                            >
                              {resource.label}
                            </td>
                            {catalog.actions.map((action) => (
                              <td key={action} style={{ textAlign: 'center' }}>
                                <Checkbox
                                  size="sm"
                                  disabled={readOnly}
                                  checked={readOnly || hasAction(resource.key, action)}
                                  onChange={() => toggleAction(resource.key, action)}
                                />
                              </td>
                            ))}
                            <td style={{ textAlign: 'center' }}>
                              {resource.ownable ? (
                                readOnly ? (
                                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>All records</Typography>
                                ) : (
                                  <Select
                                    size="sm"
                                    value={scopes[resource.key] === 'own' ? 'own' : 'all'}
                                    disabled={(matrix[resource.key] || []).length === 0}
                                    onChange={(_e, value) => setScope(resource.key, value)}
                                    sx={{ minWidth: 110 }}
                                  >
                                    <Option value="all">All records</Option>
                                    <Option value="own">Only theirs</Option>
                                  </Select>
                                )
                              ) : (
                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>—</Typography>
                              )}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </Box>
            </>
          )}
        </Sheet>
      </Box>
    </Box>
  );
};

export default RolesPermissionsPage;
